import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import mergeFilterInputs from 'mergeFilterInputs';
import {
  usePlatesToFilterInput,
  useGetMetric,
} from 'screens/GoalShow/useGoalData';
import isDefined from 'isDefined';
import GqlClientContext from 'contexts/GqlClientContext';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';
import useBuildKpiInputs from './useBuildKpiInputs';
import { isKpiRow } from 'hooks/kpiTypeCheckers';
import metricTypeCheckers from 'types/metricTypeCheckers';
import aggregateSpecialMetrics from './aggregateSpecialMetrics';
import calculateScorecard from './calculateScorecard';
import getCurrentPeriodEndDate from './getCurrentPeriodEndDate';
import { AccountTodayOverrideContext } from '../SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import EntityDetailsContext from '../../screens/EntityDetailsShow/EntityDetailsContext';

// The in-progress period stops at today (or yesterday) unless the list shows
// the entire period. Future periods always run to their end, so when both are
// selected they're queried separately: a single query to the end of the last
// future period would give the current period its whole period's data.
const getDateScopes = ({
  currentPeriodEndDate,
  selectedPeriods,
  futurePeriods,
  todayOverride,
}: {
  currentPeriodEndDate: Scorecards.Scorecard['currentPeriodEndDate'];
  selectedPeriods: Period[];
  futurePeriods: Period[];
  todayOverride: string | undefined;
}): DateRangeInput[] => {
  const isFuturePeriod = (period: Period) =>
    futurePeriods.some((f) => f.startDate === period.startDate);
  const currentPeriods = selectedPeriods.filter((p) => !isFuturePeriod(p));
  const selectedFuturePeriods = selectedPeriods.filter(isFuturePeriod);
  const lastEndDate = selectedPeriods[selectedPeriods.length - 1].endDate;

  if (
    currentPeriodEndDate === 'period' ||
    currentPeriods.length === 0 ||
    selectedFuturePeriods.length === 0
  ) {
    return [
      {
        startDate: selectedPeriods[0].startDate,
        endDate:
          currentPeriodEndDate === 'period' || currentPeriods.length === 0
            ? lastEndDate
            : getCurrentPeriodEndDate({ currentPeriodEndDate, todayOverride }),
        dateField: 'date',
      },
    ];
  }

  return [
    {
      startDate: currentPeriods[0].startDate,
      endDate: getCurrentPeriodEndDate({ currentPeriodEndDate, todayOverride }),
      dateField: 'date',
    },
    {
      startDate: selectedFuturePeriods[0].startDate,
      endDate: lastEndDate,
      dateField: 'date',
    },
  ];
};

// Combines the per-date-scope results. The scopes don't overlap, so each
// KPI's trends concatenate. Only the trend is read, so the first scope's
// value is kept.
const mergeResultsById = (
  resultsPerScope: Goals.MetricResult[][],
): Goals.MetricResult[] => {
  const merged = new Map<string, Goals.MetricResult>();
  resultsPerScope.flat().forEach((result) => {
    const existing = merged.get(result.id);
    merged.set(
      result.id,
      existing
        ? {
            ...existing,
            trend: [...(existing.trend ?? []), ...(result.trend ?? [])],
          }
        : result,
    );
  });
  return Array.from(merged.values());
};

const useScorecardData = ({
  scorecard,
  selectedPeriods,
  futurePeriods,
  isKpisDisabled,
  selectedQuickFilters,
}: {
  scorecard: Scorecards.Scorecard;
  selectedPeriods: Period[];
  futurePeriods: Period[];
  isKpisDisabled?: boolean;
  selectedQuickFilters: LegacySavedFilter[];
}) => {
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { client } = useContext(GqlClientContext);
  const [data, setData] = useState<Goals.MetricResult[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [scorecardInput, setScorecardInput] =
    useState<Scorecards.GetScorecardDataArgs['normalMetricsArgs']>();
  const entityDetails = useContext(EntityDetailsContext);

  const getMetric = useGetMetric();
  const platesToFilterInput = usePlatesToFilterInput();

  const buildKpiInputs = useBuildKpiInputs({ scorecard, selectedQuickFilters });

  const buildMetricArgs =
    useCallback((): Scorecards.GetScorecardDataArgs | null => {
      const specialKpis = scorecard.kpis
        .filter(isKpiRow)
        .map((kpi) => {
          const metric = getMetric(kpi.metricId);
          if (!metricTypeCheckers.isSpecialMetric(metric)) {
            return undefined;
          }
          return {
            kpi,
            metric,
          };
        })
        .filter(isDefined);

      if (
        (!scorecardInput && specialKpis.length === 0) ||
        selectedPeriods.length === 0
      ) {
        return null;
      }

      const dateScopes = getDateScopes({
        currentPeriodEndDate: scorecard.currentPeriodEndDate,
        selectedPeriods,
        futurePeriods,
        todayOverride,
      });

      const uniqueFilterMetrics = [] as Scorecards.UniqFilterMetrics[];

      const quickFilterInputs = selectedQuickFilters.map((qf) =>
        platesToFilterInput(qf.drillDowns),
      );

      specialKpis.forEach((specialKpi) => {
        const filterInput = quickFilterInputs.reduce(
          (acc, quickFilterInput) => mergeFilterInputs(acc, quickFilterInput),
          mergeFilterInputs(
            platesToFilterInput(specialKpi.kpi.drillDowns),
            platesToFilterInput(specialKpi.kpi.scopeDrillDowns),
            specialKpi.kpi.isEntityFiltersDisabled
              ? undefined
              : entityDetails?.entityFilter,
          ),
        );

        const uniqueMetricsGroupIndex = uniqueFilterMetrics.findIndex(
          (filterMetrics) =>
            _.isEqual(filterMetrics.filters, filterInput) &&
            filterMetrics.queryName === specialKpi.metric.query,
        );

        if (uniqueMetricsGroupIndex === -1) {
          uniqueFilterMetrics.push({
            filters: filterInput,
            queryName: specialKpi.metric.query,
            kpi: [
              {
                kpiId: specialKpi.kpi.id,
                specialMetric: specialKpi.metric,
              },
            ],
          });
        } else {
          uniqueFilterMetrics[uniqueMetricsGroupIndex].kpi.push({
            kpiId: specialKpi.kpi.id,
            specialMetric: specialKpi.metric,
          });
        }
      });

      return {
        client,
        normalMetricsArgs: scorecardInput || undefined,
        specialMetricRequests: uniqueFilterMetrics.map((filterMetrics) => ({
          kpis: filterMetrics.kpi,
          filters: filterMetrics.filters,
          queryName: filterMetrics.queryName,
          dateScopes,
          interval: scorecard.cadence,
        })),
      };
    }, [
      client,
      entityDetails?.entityFilter,
      futurePeriods,
      getMetric,
      platesToFilterInput,
      scorecard.cadence,
      scorecard.currentPeriodEndDate,
      scorecard.kpis,
      scorecardInput,
      selectedPeriods,
      selectedQuickFilters,
      todayOverride,
    ]);

  const [scorecardArgs, setScorecardArgs] =
    useState<Scorecards.GetScorecardDataArgs | null>(() => buildMetricArgs());

  useEffect(() => {
    setScorecardArgs(buildMetricArgs());
  }, [buildMetricArgs]);

  const getMetricsDataCallback = useCallback(
    async (
      args: Scorecards.GetScorecardDataArgs | null,
    ): Promise<Goals.MetricResult[]> => {
      if (!args) {
        return [];
      }

      const normalMetricsArgs = args.normalMetricsArgs;

      const calculateScoresResponse = normalMetricsArgs
        ? Promise.all(
            normalMetricsArgs.dateScopes.map((dateScope) =>
              calculateScorecard({
                scorecardInputArgs: {
                  metrics: normalMetricsArgs.metrics,
                  dateScope,
                  interval: normalMetricsArgs.interval,
                },
                client,
              }),
            ),
          ).then(mergeResultsById)
        : Promise.resolve([]);

      const specialMetricResponses = await Promise.all(
        args.specialMetricRequests.map(async (r) => ({
          response: (
            await Promise.all(
              r.dateScopes.map((dateScope) =>
                aggregateSpecialMetrics({
                  client,
                  filterInput: [r.filters],
                  dateScope,
                  trendByCalendarInterval:
                    r.interval === 'fiscalPeriod' ? undefined : r.interval,
                  trendByFiscalInterval:
                    r.interval === 'fiscalPeriod' ? r.interval : undefined,
                  queryName: r.queryName,
                }),
              ),
            )
          ).flat(),
          kpis: r.kpis,
        })),
      );

      const specialMetricResults: {
        id: string;
        trend: {
          date: string;
        }[];
      }[] = [];

      specialMetricResponses.forEach((metricResponse) => {
        metricResponse.kpis.forEach(({ specialMetric, kpiId }) => {
          const specialMetricRow = {
            id: kpiId,
            trend: selectedPeriods.map((period) => {
              const metricPeriodResponse = metricResponse.response.find(
                (res) => res.date === period.startDate,
              );
              return {
                date: period.startDate,
                value: metricPeriodResponse
                  ? metricPeriodResponse[specialMetric.id]
                  : undefined,
              };
            }),
          };
          specialMetricResults.push(specialMetricRow);
        });
      });

      const calculateScoresResults = await calculateScoresResponse;

      return [...calculateScoresResults, ...specialMetricResults];
    },
    [client, selectedPeriods],
  );

  const responseHandler = useCallback((response: Goals.MetricResult[]) => {
    setIsLoading(false);
    setData(response);
  }, []);

  useLockedDebouncedEffect({
    args: scorecardArgs,
    callback: getMetricsDataCallback,
    responseHandler,
  });

  const buildScorecardInput = useCallback(() => {
    const metrics = buildKpiInputs();

    if (
      selectedPeriods.length === 0 ||
      metrics.length === 0 ||
      isKpisDisabled
    ) {
      return;
    }

    const dateScopes = getDateScopes({
      currentPeriodEndDate: scorecard.currentPeriodEndDate,
      selectedPeriods,
      futurePeriods,
      todayOverride,
    });

    return {
      metrics,
      dateScopes,
      interval: scorecard.cadence,
    };
  }, [
    buildKpiInputs,
    futurePeriods,
    isKpisDisabled,
    scorecard.cadence,
    scorecard.currentPeriodEndDate,
    selectedPeriods,
    todayOverride,
  ]);

  useEffect(() => {
    const newGoalInput = buildScorecardInput();
    if (!newGoalInput) {
      return;
    }

    if (_.isEqual(scorecardInput, newGoalInput)) {
      return;
    }
    setScorecardInput(newGoalInput);
  }, [buildScorecardInput, scorecard, scorecardInput]);

  return {
    data,
    isLoading,
    scorecardInput,
  };
};

export default useScorecardData;
